import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { CONTACT, FORM_OPTIONS, INTENT_PARAM } from "@/content/site";
import { getAttribution } from "@/lib/attribution";
import { track } from "@/lib/analytics";
import { TextField, SelectField, RadioGroup } from "@/components/FormFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/leads`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CONSENT_VERSION = "2026-10-23-v1";

const LABELS = {
  intent: "What would you like to do?", name: "Name", phone: "Phone", email: "Email", preferred_market: "Preferred Market or State",
  investment_timeframe: "Investment Timeframe", liquid_capital: "Available Liquid Capital", unit_interest: "Single- or Multi-Unit Interest",
  healthcare_background: "Healthcare or physician background", message: "Message", consent: "Consent",
};

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Enter your full name.";
  const digits = v.phone.replace(/\D/g, "");
  if (!/^[0-9+().\-\s]+$/.test(v.phone) || digits.length < 10 || digits.length > 15) e.phone = "Enter a valid phone number with area code.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Enter a valid email address.";
  if (v.preferred_market.trim().length < 2) e.preferred_market = "Enter your preferred market or state.";
  if (!v.investment_timeframe) e.investment_timeframe = "Select your investment timeframe.";
  if (!v.liquid_capital) e.liquid_capital = "Select your available liquid capital.";
  if (!v.unit_interest) e.unit_interest = "Select single- or multi-unit interest.";
  if (!v.healthcare_background) e.healthcare_background = "Select whether you have a healthcare or physician background.";
  if (v.message.length > 2000) e.message = "Message must be 2,000 characters or fewer.";
  if (!v.consent) e.consent = "Please check the consent box to submit your inquiry.";
  return e;
}

export const LeadForm = ({ idPrefix = "lead" }) => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const paramIntent = INTENT_PARAM[params.get("intent")];
  const [v, setV] = useState({ intent: FORM_OPTIONS.intent[0], name: "", phone: "", email: "", preferred_market: "", investment_timeframe: "", liquid_capital: "", unit_interest: "", healthcare_background: "", message: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const mountedAt = useRef(0);
  useEffect(() => { mountedAt.current = Date.now(); }, []);
  useEffect(() => { if (paramIntent) setV((s) => ({ ...s, intent: paramIntent })); }, [paramIntent]);
  const started = useRef(false);
  const summaryRef = useRef(null);
  const id = (n) => `${idPrefix}-${n}`;

  const set = (name) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (errors[name]) setErrors((s) => ({ ...s, [name]: undefined }));
  };
  const onFocus = () => {
    if (!started.current) { started.current = true; track("form_start", { form_location: pathname }); }
  };
  const errList = useMemo(() => Object.entries(errors).filter(([, m]) => m), [errors]);

  const showErrors = (e) => {
    setErrors(e);
    track("form_validation_error", { fields: Object.keys(e).join(","), form_location: pathname });
    setTimeout(() => summaryRef.current?.focus(), 0);
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setFormError("");
    const e = validate(v);
    if (Object.keys(e).length) return showErrors(e);
    setSubmitting(true);
    const { website, ...fields } = v;
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...fields, website, consent_version: CONSENT_VERSION, elapsed_ms: Date.now() - mountedAt.current, attribution: { ...getAttribution(), submitted_from: pathname } }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 201) {
        track("generate_lead", { form_location: pathname, inquiry_type: v.intent });
        navigate("/thank-you", { state: { submitted: true } });
        return;
      }
      if (res.status === 422 && data.errors) return showErrors(data.errors);
      setFormError(data.errors?.form || "We couldn’t submit your inquiry. Please try again, or call or email us directly.");
      setTimeout(() => summaryRef.current?.focus(), 0);
    } catch {
      setFormError("Network error. Please try again, or call or email us directly.");
      setTimeout(() => summaryRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const common = (name) => ({ id: id(name), name, label: LABELS[name], value: v[name], onChange: set(name), onFocus, error: errors[name] });

  return (
    <form noValidate method="post" onSubmit={onSubmit} className="space-y-6" aria-describedby={id("required-note")} data-testid="lead-form">
      {(errList.length > 0 || formError) && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="border-2 border-[#9B1C1C] bg-[#FDF2F2] p-5 text-[#7F1D1D]" data-testid="form-error-summary">
          <p className="flex items-center gap-2 font-semibold"><AlertCircle aria-hidden="true" className="h-5 w-5" />{formError || `Please fix ${errList.length} ${errList.length === 1 ? "field" : "fields"} below:`}</p>
          {errList.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-6 text-sm">
              {errList.map(([k, m]) => <li key={k}><a href={`#${id(k)}`} className="underline">{LABELS[k] || k}: {m}</a></li>)}
            </ul>
          )}
          {formError && <p className="mt-2 text-sm">Phone: <a href={CONTACT.tel} className="underline">{CONTACT.phone}</a> · Email: <a href={CONTACT.mailto} className="underline">{CONTACT.email}</a></p>}
        </div>
      )}
      <p id={id("required-note")} className="text-sm text-stone-600">Fields marked with <span aria-hidden="true" className="text-gold-deep">*</span><span className="sr-only">an asterisk</span> are required.</p>

      <SelectField {...common("intent")} options={FORM_OPTIONS.intent} placeholder={false} />
      <div className="grid gap-6 sm:grid-cols-2">
        <TextField {...common("name")} required autoComplete="name" />
        <TextField {...common("phone")} required type="tel" autoComplete="tel" inputMode="tel" />
        <TextField {...common("email")} required type="email" autoComplete="email" />
        <TextField {...common("preferred_market")} required autoComplete="address-level1" />
        <SelectField {...common("investment_timeframe")} required options={FORM_OPTIONS.timeframe} />
        <SelectField {...common("liquid_capital")} required options={FORM_OPTIONS.capital} />
      </div>
      <RadioGroup {...common("unit_interest")} required options={FORM_OPTIONS.unit} />
      <RadioGroup {...common("healthcare_background")} required options={FORM_OPTIONS.healthcare} />
      <TextField {...common("message")} as="textarea" rows={4} maxLength={2000} />

      <div aria-hidden="true" className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor={id("website")}>Website</label>
        <input id={id("website")} name="website" type="text" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} />
      </div>

      <div>
        <div className="flex items-start gap-3">
          <input id={id("consent")} name="consent" type="checkbox" checked={v.consent} onChange={set("consent")} onFocus={onFocus} aria-invalid={!!errors.consent} aria-describedby={errors.consent ? id("consent-error") : undefined} className="mt-1 h-6 w-6 shrink-0 accent-[#161310]" data-testid="lead-form-consent" />
          <label htmlFor={id("consent")} className="text-sm text-stone-600">
            <span aria-hidden="true" className="text-gold-deep">* </span>By checking this box and submitting, I provide my electronic signature and consent for VidaVital Medical and its franchise-development representatives to contact me at the phone number and email I provided about franchise ownership, including by calls, text messages (which may use automated technology), and email. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply STOP to opt out of texts and HELP for help. I have read and agree to the <Link to="/terms-of-use" className="text-link" data-testid="lead-form-terms-link">Terms of Use</Link> and acknowledge the <Link to="/privacy-policy" className="text-link" data-testid="lead-form-privacy-link">Privacy Policy</Link>.
          </label>
        </div>
        {errors.consent && <p id={id("consent-error")} className="mt-2 text-sm font-semibold text-[#9B1C1C]" data-testid="lead-form-consent-error">{errors.consent}</p>}
      </div>

      <button type="submit" className="btn-gold w-full sm:w-auto" disabled={submitting} data-testid="lead-form-submit">
        {submitting && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
        {submitting ? "Submitting…" : "Explore the Franchise"}
      </button>
    </form>
  );
};
